import type { MonetizationSettings } from "./settings";

/**
 * Placement-anchor registry (PR-5, freemium-monetization-remediation plan
 * § 4b). Each surface that supports payload-tunable placement lists its
 * candidate anchor ids here, FIRST entry always the surface's default/current
 * mount point. Registering a surface here is a code change ("code once");
 * WHICH anchor is active is tunable live via the `monetization-settings`
 * PostHog payload (`surfaces.<surface>.anchor`) — zero deploy.
 *
 * Guardrail: only one anchor may render per surface per page. Enforced by
 * construction — `resolveSurfaceAnchor` returns exactly one id, and every
 * `<AnchorSlot>` for a surface renders only when its own `anchor` prop
 * matches that single resolved value.
 */
// PR-5 (ux-seeker-journey-remediation, Task 5.4): default flipped from
// `sidebar_top` to `below_description` on 2026-08-11. Session replay caught a
// high-intent reader exiting through the mobile affiliate unit instead of
// applying — `sidebar_top` mounts it right after the summary card, in the
// same scroll region as the always-visible fixed-bottom Apply bar on mobile,
// and (via the matching desktop mount further down the sticky sidebar column)
// still co-visible with the desktop sticky Apply CTA the whole time that
// column is on screen. `below_description` mounts it in the main column,
// past the point where the sidebar's sticky CTA has scrolled out of view on
// desktop, and lower in the mobile scroll — same unit, no code move needed
// elsewhere, because the placement was ALREADY payload-tunable (this array's
// order IS the tunable default). Revert live via the `monetization-settings`
// PostHog payload (`surfaces.job_sidebar.anchor`) if this regresses CTR.
export const SURFACE_ANCHORS = {
	job_sidebar: ["below_description", "sidebar_top", "above_related"],
} as const satisfies Record<string, readonly string[]>;

export type AnchoredSurface = keyof typeof SURFACE_ANCHORS;

export function isAnchoredSurface(surface: string): surface is AnchoredSurface {
	return surface in SURFACE_ANCHORS;
}

/**
 * Resolve which single anchor a surface should render at. Falls back to the
 * surface's default (first registered) anchor when the configured value is
 * absent OR names an anchor not registered for this surface — an invalid
 * PostHog payload value can never blank the unit or render two at once.
 */
export function resolveSurfaceAnchor(
	surfaces: MonetizationSettings["surfaces"],
	surface: AnchoredSurface,
): string {
	const candidates: readonly string[] = SURFACE_ANCHORS[surface];
	const configured = surfaces[surface]?.anchor;
	return configured && candidates.includes(configured)
		? configured
		: candidates[0];
}

/**
 * Resolve a payload-tunable list-position index (e.g. `feed_inline`'s in-feed
 * ad row). Falls back to `fallback` (the surface's current hardcoded index)
 * when absent — schema already guarantees a present value is a non-negative
 * integer.
 */
export function resolveSurfacePositionIndex(
	surfaces: MonetizationSettings["surfaces"],
	surface: string,
	fallback: number,
): number {
	const configured = surfaces[surface]?.positionIndex;
	return typeof configured === "number" ? configured : fallback;
}

/** Current hardcoded feed_inline row index — the default when the payload
 * doesn't set `surfaces.feed_inline.positionIndex`. */
export const FEED_INLINE_DEFAULT_POSITION_INDEX = 8;

/**
 * Default row index for the `feed-inline-display` AdSlot (PR C,
 * 2026-09-28) — the WR spec's row 12. Payload-tunable via
 * `surfaces.feed_inline_display.positionIndex`, same mechanism as
 * `FEED_INLINE_DEFAULT_POSITION_INDEX` above; kept a separate constant/surface
 * so the affiliate card (row 8) and this display unit never share a knob.
 */
export const FEED_INLINE_DISPLAY_DEFAULT_POSITION_INDEX = 12;
